import type * as PIXI from 'pixi.js'
import { useCallback } from 'react'
import { damageStage, POWER_METER, POWER_WINDUP_MS } from '#app/utils/pinata/game.ts'
import { playSfx } from '#app/utils/pinata/sfx.ts'
import { buildShards, mulberry32, type Shard } from '#app/utils/pinata/shards.ts'
import { type PinataStore, type PinataStoreEvent } from '#app/utils/pinata/store.ts'

// Everything is laid out in a fixed design space and the world container is
// scaled to fit the canvas, so physics constants never depend on screen size.
const DESIGN_W = 900
const DESIGN_H = 640
/** Width that must stay visible: the piñata plus room for the bat */
const FIT_W = 560
const ROPE_LENGTH = 120
const MAX_PINATA = 360
const GRAVITY = 1600
const SHARD_COLS = 8
const SHARD_ROWS = 8
const CONFETTI = [0xff4d6d, 0xffd166, 0x06d6a0, 0x118ab2, 0xef476f, 0xf78c6b, 0x9b5de5]
// Crepe-paper layers that show through cracks and holes.
const CREPE = [0xff5fa2, 0xffb703, 0x3ec1d3, 0x8338ec, 0x06d6a0, 0xfb5607]
const CANDY = [0xff006e, 0xffbe0b, 0x3a86ff, 0x8ac926, 0xff5d8f]
// Where the bat's grip sits relative to the aim point (mirrored per side).
const BAT_OFFSET = { x: 150, y: 110 }
const BAT_WINDUP = 1.25
const BAT_SWING_SECONDS = 0.3
const METER_W = 380
const METER_H = 22
/** Just under the tallest piñata */
const METER_Y = ROPE_LENGTH + MAX_PINATA + 44

type ShardView = {
	shard: Shard
	/** Holds the image mesh plus its crack outline, so they move together */
	node: PIXI.Container
	mesh: PIXI.MeshSimple
	edge: PIXI.Graphics
	home: { x: number; y: number }
	drift: { x: number; y: number; r: number }
	attached: boolean
}

type Particle = {
	obj: PIXI.Container
	vx: number
	vy: number
	vr: number
	life: number
	fadeAfter: number
	gravity: number
	grow?: number
}

export function PinataStage({
	imageSrc,
	store,
	seed = 1,
}: {
	imageSrc: string
	store: PinataStore
	seed?: number
}) {
	const mount = useCallback(
		(el: HTMLDivElement | null) => {
			if (!el) return
			let disposed = false
			let cleanup: (() => void) | undefined
			void startStage(el, imageSrc, store, seed).then((dispose) => {
				if (disposed) dispose()
				else cleanup = dispose
			})
			return () => {
				disposed = true
				cleanup?.()
			}
		},
		[imageSrc, store, seed],
	)

	return (
		<div
			ref={mount}
			data-testid="pinata-stage"
			className="relative h-full w-full cursor-none touch-none select-none"
			aria-hidden
		/>
	)
}

function loadImage(src: string) {
	return new Promise<HTMLImageElement>((resolve, reject) => {
		const img = new Image()
		img.crossOrigin = 'anonymous'
		img.onload = () => resolve(img)
		img.onerror = () => reject(new Error(`Could not load piñata image ${src}`))
		img.src = src
	})
}

async function startStage(
	el: HTMLDivElement,
	imageSrc: string,
	store: PinataStore,
	seed: number,
) {
	const [pixi, img] = await Promise.all([import('pixi.js'), loadImage(imageSrc)])
	const { Application, Container, Graphics, MeshSimple, Sprite, Text, Texture } = pixi

	const app = new Application()
	await app.init({
		resizeTo: el,
		backgroundAlpha: 0,
		antialias: true,
		autoDensity: true,
		resolution: Math.min(window.devicePixelRatio || 1, 2),
	})
	el.appendChild(app.canvas)
	el.dataset.ready = 'true'

	const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
	const motion = reducedMotion ? 0.35 : 1
	const rand = mulberry32(seed * 7919)

	const texture = Texture.from(img)
	const fit = Math.min(MAX_PINATA / img.naturalWidth, MAX_PINATA / img.naturalHeight)
	const pw = img.naturalWidth * fit
	const ph = img.naturalHeight * fit

	const candyTextures = CANDY.map((color) =>
		app.renderer.generateTexture({ target: drawCandy(Graphics, color), resolution: 2 }),
	)
	const starTexture = app.renderer.generateTexture({ target: drawStar(Graphics), resolution: 2 })

	// world > swing (rotates at the rope anchor) > rope + body (the piñata)
	const world = new Container()
	const swing = new Container()
	const rope = new Graphics()
		.moveTo(0, 0)
		.lineTo(0, ROPE_LENGTH + 4)
		.stroke({ width: 4, color: 0x8b6b3d })
	const body = new Container()
	body.pivot.set(pw / 2, 0)
	body.position.set(0, ROPE_LENGTH)
	swing.position.set(DESIGN_W / 2, 0)
	swing.addChild(rope, body)

	const beam = new Graphics().rect(DESIGN_W / 2 - 80, -12, 160, 16).fill(0x5b4327)
	const debris = new Container()
	const bat = drawBat(Graphics)
	const reticle = new Graphics()
		.circle(0, 0, 9)
		.stroke({ width: 3, color: 0x000000, alpha: 0.5 })
		.circle(0, 0, 9)
		.stroke({ width: 1.5, color: 0xffffff })
		.circle(0, 0, 1.5)
		.fill(0xffffff)
	bat.visible = false
	reticle.visible = false
	const meter = drawMeter(Container, Graphics, Text)
	meter.root.position.set(DESIGN_W / 2, METER_Y)
	meter.root.visible = store.getState().rules.powerSwing
	world.addChild(beam, swing, meter.root, debris, bat, reticle)
	app.stage.addChild(world)

	// ---- piñata body ------------------------------------------------------
	let shardViews: ShardView[] = []

	function buildBody() {
		for (const child of body.removeChildren()) child.destroy({ children: true })
		body.addChild(drawCrepe(Graphics, pw, ph))
		shardViews = buildShards({ cols: SHARD_COLS, rows: SHARD_ROWS, seed }).map((shard) => {
			const cx = shard.centroid[0] * pw
			const cy = shard.centroid[1] * ph
			const local = shard.points.flatMap(([u, v]) => [u * pw - cx, v * ph - cy])
			const mesh = new MeshSimple({
				texture,
				vertices: new Float32Array(local),
				uvs: new Float32Array(shard.points.flat()),
				indices: new Uint32Array([0, 1, 2]),
			})
			// Two-tone crack line: dark reads on light images, light on dark ones.
			const edge = new Graphics()
				.poly(local)
				.stroke({ width: 2.4, color: 0x120812, alpha: 0.75, join: 'round' })
				.poly(local)
				.stroke({ width: 0.9, color: 0xfff6e0, alpha: 0.8, join: 'round' })
			edge.visible = false
			const node = new Container()
			node.addChild(mesh, edge)
			node.position.set(cx, cy)
			body.addChild(node)
			const out = { x: shard.centroid[0] - 0.5, y: shard.centroid[1] - 0.5 }
			return {
				shard,
				node,
				mesh,
				edge,
				home: { x: cx, y: cy },
				drift: {
					x: out.x * 2.2 + (rand() - 0.5) * 1.2,
					y: out.y * 2.2 + (rand() - 0.5) * 1.2,
					r: (rand() - 0.5) * 0.02,
				},
				attached: true,
			}
		})
	}
	buildBody()

	function applyCracks(stage: number) {
		for (const view of shardViews) {
			if (!view.attached) continue
			view.node.position.set(
				view.home.x + view.drift.x * stage,
				view.home.y + view.drift.y * stage,
			)
			view.node.rotation = view.drift.r * stage
		}
	}

	/** Cracks spread out from wherever it gets hit. */
	function revealCracks(u: number, v: number, radius: number) {
		for (const view of shardViews) {
			const [cu, cv] = view.shard.centroid
			if (view.attached && Math.hypot(cu - u, cv - v) <= radius) view.edge.visible = true
		}
	}

	// ---- particles --------------------------------------------------------
	const particles: Particle[] = []

	function addParticle(obj: PIXI.Container, p: Omit<Particle, 'obj'>) {
		debris.addChild(obj)
		particles.push({ obj, ...p })
	}

	function detach(view: ShardView, vx: number, vy: number) {
		if (!view.attached) return
		view.attached = false
		const pos = world.toLocal(view.node.position, body)
		view.node.rotation += swing.rotation
		view.node.position.copyFrom(pos)
		addParticle(view.node, {
			vx,
			vy,
			vr: (rand() - 0.5) * 10,
			life: 2.2,
			fadeAfter: 1.2,
			gravity: GRAVITY,
		})
	}

	function burstConfetti(x: number, y: number, count: number, speed: number) {
		for (let i = 0; i < count; i++) {
			const bit = new Sprite(Texture.WHITE)
			bit.anchor.set(0.5)
			bit.width = 4 + rand() * 6
			bit.height = 6 + rand() * 8
			bit.tint = CONFETTI[Math.floor(rand() * CONFETTI.length)]!
			bit.position.set(x, y)
			const angle = rand() * Math.PI * 2
			const v = speed * (0.3 + rand() * 0.7)
			addParticle(bit, {
				vx: Math.cos(angle) * v,
				vy: Math.sin(angle) * v - speed * 0.4,
				vr: (rand() - 0.5) * 14,
				life: 1.6 + rand() * 1.4,
				fadeAfter: 0.8,
				gravity: GRAVITY * 0.45,
			})
		}
	}

	function spillCandy(x: number, y: number, count: number, spread: number) {
		for (let i = 0; i < count; i++) {
			const candy = new Sprite(candyTextures[Math.floor(rand() * candyTextures.length)]!)
			candy.anchor.set(0.5)
			candy.scale.set(0.5 + rand() * 0.25)
			candy.rotation = rand() * Math.PI * 2
			candy.position.set(x + (rand() - 0.5) * 20, y + (rand() - 0.5) * 20)
			const angle = -Math.PI / 2 + (rand() - 0.5) * Math.PI * 1.6
			const v = spread * (0.25 + rand() * 0.75)
			addParticle(candy, {
				vx: Math.cos(angle) * v,
				vy: Math.sin(angle) * v,
				vr: (rand() - 0.5) * 12,
				life: 2.4 + rand(),
				fadeAfter: 0.6,
				gravity: GRAVITY,
			})
		}
	}

	function impactFlash(x: number, y: number, big = false) {
		const star = new Sprite(starTexture)
		star.anchor.set(0.5)
		star.position.set(x, y)
		star.rotation = rand() * Math.PI
		star.scale.set(big ? 0.9 : 0.45)
		addParticle(star, { vx: 0, vy: 0, vr: 2, life: 0.18, fadeAfter: 0.14, gravity: 0, grow: big ? 5 : 3 })
	}

	function popText(x: number, y: number, label: string, color: number, fontSize = 30) {
		const text = new Text({
			text: label,
			style: {
				fill: color,
				fontSize,
				fontWeight: '900',
				fontFamily: 'system-ui, sans-serif',
				stroke: { color: 0x000000, width: 5 },
			},
		})
		text.anchor.set(0.5)
		text.position.set(x, y)
		addParticle(text, { vx: (rand() - 0.5) * 60, vy: -220, vr: 0, life: 0.9, fadeAfter: 0.4, gravity: 300 })
	}

	// ---- motion state -----------------------------------------------------
	let angle = 0
	let angularVel = 0
	let squash = 0
	let squashVel = 0
	let shake = 0
	let flash = 0
	/** Meter outline glow after a power swing: >0 green (crit), <0 red (weak) */
	let meterGlow = 0
	/** 0..1 through a swing; 1 means resting in the wound-up pose */
	let batT = 1
	let batSide = 1
	const batAim = { x: DESIGN_W / 2, y: ROPE_LENGTH + ph / 2 }

	function uvToWorld(u: number, v: number) {
		return world.toLocal(body.toGlobal({ x: u * pw, y: v * ph }))
	}

	/** Point the bat at a spot, swinging in from whichever side it's on. */
	function aimBat(x: number, y: number) {
		batAim.x = x
		batAim.y = y
		batSide = x < DESIGN_W / 2 ? -1 : 1
	}

	function onStoreEvent(e: PinataStoreEvent) {
		if (e.type === 'reset') {
			for (const p of particles.splice(0)) p.obj.destroy({ children: true })
			angle = 0
			angularVel = 0
			buildBody()
			meter.root.visible = store.getState().rules.powerSwing
			return
		}
		const { event, x: u, y: v } = e
		if (event.type === 'ignored') return
		const hitPos = uvToWorld(u, v)

		const crit = event.strength === 'crit'
		const weak = event.strength === 'weak'
		if (crit) meterGlow = 1
		else if (weak) meterGlow = -0.6

		if (event.type === 'explode') {
			for (const view of shardViews) {
				// Chipped-off shards may already be destroyed by the particle loop.
				if (!view.attached) continue
				const pos = world.toLocal(view.node.position, body)
				const dx = pos.x - hitPos.x
				const dy = pos.y - hitPos.y
				const dist = Math.max(Math.hypot(dx, dy), 1)
				const power = 500 + rand() * 700
				detach(view, (dx / dist) * power, (dy / dist) * power - 450)
			}
			// The crepe lining goes too, so only the rope is left swinging.
			for (const child of body.removeChildren()) child.destroy({ children: true })
			impactFlash(hitPos.x, hitPos.y, true)
			burstConfetti(hitPos.x, hitPos.y, reducedMotion ? 60 : 180, 900)
			spillCandy(hitPos.x, hitPos.y, reducedMotion ? 20 : 45, 750)
			popText(hitPos.x, hitPos.y - 40, crit ? 'CRITICAL BOOM!' : 'BOOM!', 0xffd166)
			meter.root.visible = false
			shake = 22 * motion
			playSfx('boom')
			return
		}

		// A hit: push the swing away from the side that was struck. Crits hit
		// harder, weak power-swing hits barely nudge it.
		const power = crit ? 1.8 : weak ? 0.5 : 1
		angularVel += ((0.5 - u) * 2.6 + (rand() - 0.5) * 0.4) * power * motion
		squashVel += 3.5 * power * motion
		flash = weak ? 0.5 : 1
		shake = Math.max(shake, (crit ? 12 : weak ? 2 : 5) * motion)
		impactFlash(hitPos.x, hitPos.y, crit)
		if (crit) {
			burstConfetti(hitPos.x, hitPos.y, 28, 560)
			spillCandy(hitPos.x, hitPos.y, 3, 420)
			popText(hitPos.x, hitPos.y - 24, `CRIT! -${event.damage}`, 0xffd166, 42)
			playSfx('crit')
		} else if (weak) {
			burstConfetti(hitPos.x, hitPos.y, 3, 240)
			popText(hitPos.x, hitPos.y - 16, `-${event.damage}`, 0xc9c3d9, 22)
			playSfx('weak')
		} else {
			burstConfetti(hitPos.x, hitPos.y, 8, 380)
			popText(
				hitPos.x,
				hitPos.y - 20,
				event.combo > 0 ? `-${event.damage} x${event.combo + 1}` : `-${event.damage}`,
				event.combo > 0 ? 0xffd166 : 0xffffff,
			)
			playSfx('hit', event.combo)
		}
		revealCracks(u, v, 0.1 + event.stage * 0.05)
		if (event.stageChanged) {
			applyCracks(event.stage)
			const nearest = shardViews
				.filter((view) => view.attached)
				.sort(
					(a, b) =>
						Math.hypot(a.shard.centroid[0] - u, a.shard.centroid[1] - v) -
						Math.hypot(b.shard.centroid[0] - u, b.shard.centroid[1] - v),
				)
				.slice(0, 2 + event.stage)
			for (const view of nearest) {
				detach(view, (rand() - 0.5) * 300, -150 - rand() * 200)
			}
			spillCandy(hitPos.x, hitPos.y, 3 + event.stage * 2, 380)
			playSfx('crunch')
		}
	}
	const unsubscribe = store.subscribe(onStoreEvent)
	applyCracks(damageStage(store.getState()))

	// ---- input ------------------------------------------------------------
	function swingBat() {
		batT = 0
		bat.visible = true
	}

	app.stage.eventMode = 'static'
	app.stage.hitArea = app.screen
	app.stage.on('pointermove', (e) => {
		const p = world.toLocal(e.global)
		reticle.position.copyFrom(p)
		reticle.visible = e.pointerType === 'mouse'
		bat.visible = true
		// Don't yank the bat around mid-swing.
		if (batT >= 1) aimBat(p.x, p.y)
	})
	app.stage.on('pointerdown', (e) => {
		const p = world.toLocal(e.global)
		aimBat(p.x, p.y)
		swingBat()
		const local = body.toLocal(e.global)
		const u = local.x / pw
		const v = local.y / ph
		const onPinata = u >= 0 && u <= 1 && v >= 0 && v <= 1
		if (onPinata && store.getState().status === 'intact') store.swing(u, v)
		else playSfx('whoosh')
	})
	app.stage.on('pointerleave', (e) => {
		// On touch, "leave" fires right after the tap; keep the bat so the swing shows.
		if (e.pointerType !== 'mouse') return
		bat.visible = false
		reticle.visible = false
	})

	function onKeyDown(e: KeyboardEvent) {
		if (e.code !== 'Space' && e.key !== ' ') return
		const target = e.target as HTMLElement | null
		if (target?.closest('input, textarea, select, button, a, [contenteditable]')) return
		e.preventDefault()
		if (e.repeat || store.getState().status !== 'intact') return
		const u = 0.3 + rand() * 0.4
		const v = 0.3 + rand() * 0.4
		const p = uvToWorld(u, v)
		aimBat(p.x, p.y)
		swingBat()
		store.swing(u, v)
	}
	window.addEventListener('keydown', onKeyDown)

	// ---- frame loop -------------------------------------------------------
	app.ticker.add((ticker) => {
		const dt = Math.min(ticker.deltaMS / 1000, 1 / 20)

		// Fit the design space to the canvas, hanging from the top. Narrow screens
		// only need the middle strip (piñata + bat), so the empty sides get cropped.
		const scale = Math.min(app.screen.width / FIT_W, app.screen.height / DESIGN_H)
		world.scale.set(scale)
		const shakeX = shake ? (Math.random() - 0.5) * shake : 0
		const shakeY = shake ? (Math.random() - 0.5) * shake : 0
		world.position.set(
			(app.screen.width - DESIGN_W * scale) / 2 + shakeX,
			16 * scale + shakeY,
		)
		shake = shake < 0.3 ? 0 : shake * Math.pow(0.001, dt)

		// Damped pendulum, with a slow idle sway so it never looks frozen.
		const idle = Math.sin(performance.now() / 900) * 0.02
		angularVel += (-9 * Math.sin(angle - idle) - 1.6 * angularVel) * dt
		angle += angularVel * dt
		swing.rotation = angle

		// Squash spring on the body.
		squashVel += (-180 * squash - 12 * squashVel) * dt
		squash += squashVel * dt
		body.scale.set(1 + squash * 0.08, 1 - squash * 0.08)

		if (flash > 0) {
			flash = Math.max(0, flash - dt * 7)
			const gb = Math.round(255 - 110 * flash)
			const tint = (255 << 16) | (gb << 8) | gb
			for (const view of shardViews) if (view.attached) view.mesh.tint = tint
		}

		if (meter.root.visible) {
			const state = store.getState()
			// Dimmed while winding up: a swing now can only be a weak hit.
			const cooling =
				state.status === 'intact' &&
				state.lastHitAt !== null &&
				performance.now() - state.lastHitAt < POWER_WINDUP_MS
			meter.marker.x = -METER_W / 2 + METER_W * store.getMeter()
			meter.marker.alpha = cooling ? 0.35 : 1
			meterGlow = Math.abs(meterGlow) < 0.02 ? 0 : meterGlow * Math.pow(0.02, dt)
			meter.glow.tint = meterGlow >= 0 ? 0x2ee59d : 0xff4d6d
			meter.glow.alpha = Math.abs(meterGlow)
		}

		// Bat: grip sits below and outside the aim point. Wound up at rest; a
		// swing snaps the head onto the aim point fast, then eases back.
		batT = Math.min(1, batT + dt / BAT_SWING_SECONDS)
		bat.position.set(batAim.x + batSide * BAT_OFFSET.x, batAim.y + BAT_OFFSET.y)
		const impact = Math.atan2(-batSide * BAT_OFFSET.x, BAT_OFFSET.y)
		const windup = impact + batSide * BAT_WINDUP
		const k = batT < 0.2 ? 1 - (1 - batT / 0.2) ** 2 : 1 - easeInOut((batT - 0.2) / 0.8)
		bat.rotation = windup + (impact - windup) * k

		for (let i = particles.length - 1; i >= 0; i--) {
			const p = particles[i]!
			p.vy += p.gravity * dt
			p.obj.x += p.vx * dt
			p.obj.y += p.vy * dt
			p.obj.rotation += p.vr * dt
			if (p.grow) p.obj.scale.set(p.obj.scale.x + p.grow * dt)
			p.life -= dt
			if (p.life < p.fadeAfter) p.obj.alpha = Math.max(0, p.life / p.fadeAfter)
			if (p.life <= 0 || p.obj.y > DESIGN_H + 400) {
				p.obj.destroy({ children: true })
				particles.splice(i, 1)
			}
		}
	})

	return () => {
		unsubscribe()
		delete el.dataset.ready
		window.removeEventListener('keydown', onKeyDown)
		app.destroy({ removeView: true }, { children: true, texture: true, textureSource: true })
		for (const t of [...candyTextures, starTexture]) t.destroy(true)
	}
}

function easeInOut(t: number) {
	return t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2
}

/** Striped crepe paper: what you see through the cracks and holes. */
function drawCrepe(Graphics: typeof PIXI.Graphics, w: number, h: number) {
	const g = new Graphics()
	const band = 14
	for (let y = 0, i = 0; y < h - 6; y += band, i++) {
		g.rect(3, 3 + y, w - 6, Math.min(band, h - 6 - y)).fill(CREPE[i % CREPE.length]!)
		// Fringe: a row of darker teeth along each band's lower edge.
		for (let x = 3; x < w - 6; x += 8) {
			g.poly([x, 3 + y + band - 5, x + 4, 3 + y + band, x + 8, 3 + y + band - 5]).fill({
				color: 0x000000,
				alpha: 0.18,
			})
		}
	}
	return g
}

function drawCandy(Graphics: typeof PIXI.Graphics, color: number) {
	return new Graphics()
		.poly([-9, 0, -18, -7, -18, 7])
		.fill(color)
		.poly([9, 0, 18, -7, 18, 7])
		.fill(color)
		.ellipse(0, 0, 11, 8)
		.fill(color)
		.ellipse(-3, -3, 4, 2.2)
		.fill({ color: 0xffffff, alpha: 0.6 })
		.ellipse(0, 0, 11, 8)
		.stroke({ width: 1.5, color: 0x000000, alpha: 0.35 })
}

function drawStar(Graphics: typeof PIXI.Graphics) {
	const points: number[] = []
	for (let i = 0; i < 16; i++) {
		const r = i % 2 === 0 ? 40 : 15
		const a = (i / 16) * Math.PI * 2
		points.push(Math.cos(a) * r, Math.sin(a) * r)
	}
	return new Graphics()
		.poly(points)
		.fill(0xfff3b0)
		.poly(points)
		.stroke({ width: 3, color: 0xffffff })
		.circle(0, 0, 12)
		.fill(0xffffff)
}

/** A tapered wooden club, grip at the origin and head pointing up (-y). */
function drawBat(Graphics: typeof PIXI.Graphics) {
	return new Graphics()
		.poly([-5, 0, 5, 0, 13, -165, 0, -184, -13, -165])
		.fill(0xc68642)
		.poly([-5, 0, 5, 0, 13, -165, 0, -184, -13, -165])
		.stroke({ width: 3, color: 0x5b3a1e, join: 'round' })
		.poly([-10.4, -118, 10.4, -118, 11.2, -130, -11.2, -130])
		.fill(0xff4d6d)
		.poly([-11.6, -138, 11.6, -138, 12.2, -148, -12.2, -148])
		.fill(0x3ec1d3)
		.roundRect(-6.5, -46, 13, 50, 4)
		.fill(0x2b2b2b)
}

/** The power swing meter: red track, green sweet spot, and a sliding marker. */
function drawMeter(
	Container: typeof PIXI.Container,
	Graphics: typeof PIXI.Graphics,
	Text: typeof PIXI.Text,
) {
	const root = new Container()
	const left = -METER_W / 2
	const zoneX = left + METER_W * POWER_METER.zoneStart
	const zoneW = METER_W * (POWER_METER.zoneEnd - POWER_METER.zoneStart)
	const track = new Graphics()
		.roundRect(left, -METER_H / 2, METER_W, METER_H, METER_H / 2)
		.fill(0x7a1f3d)
		.rect(zoneX, -METER_H / 2, zoneW, METER_H)
		.fill(0x2ee59d)
		.rect(zoneX + zoneW / 2 - 1, -METER_H / 2, 2, METER_H)
		.fill({ color: 0xffffff, alpha: 0.6 })
		.roundRect(left, -METER_H / 2, METER_W, METER_H, METER_H / 2)
		.stroke({ width: 2, color: 0xffffff, alpha: 0.6 })
	const glow = new Graphics()
		.roundRect(left - 4, -METER_H / 2 - 4, METER_W + 8, METER_H + 8, METER_H / 2 + 4)
		.stroke({ width: 4, color: 0xffffff })
	glow.alpha = 0
	const marker = new Graphics()
		.poly([-9, -METER_H / 2 - 12, 9, -METER_H / 2 - 12, 0, -METER_H / 2 - 2])
		.fill(0xffffff)
		.roundRect(-3, -METER_H / 2 - 3, 6, METER_H + 6, 3)
		.fill(0xffffff)
		.roundRect(-3, -METER_H / 2 - 3, 6, METER_H + 6, 3)
		.stroke({ width: 1.5, color: 0x000000, alpha: 0.6 })
	const label = new Text({
		text: 'POWER SWING: hit in the green!',
		style: {
			fill: 0xffffff,
			fontSize: 15,
			fontWeight: '800',
			fontFamily: 'system-ui, sans-serif',
			stroke: { color: 0x000000, width: 3 },
		},
	})
	label.anchor.set(0.5, 0)
	label.position.set(0, METER_H / 2 + 8)
	root.addChild(glow, track, marker, label)
	return { root, marker, glow }
}
